import { useContext, useEffect, useMemo, useState } from 'react';
import './Hochwasserschutz.scss';
import { getBreite, getFirstUnrestrictedVariationWert, getSelectedVariationWert } from '../../utils/app-data';
import AppContext, { AppData } from '../../AppContext';
import { ValidationMessage, validateBreite } from '../../utils/validations';
import aufWand from '../../assets/img/hochwasserschutz/auf-wand.jpg';
import inLaibung from '../../assets/img/hochwasserschutz/in-laibung.jpg';
import lAufWandRInLaibung from '../../assets/img/hochwasserschutz/l-auf-wand-r-in-laibung.jpg';
import lInLaibungRAufWand from '../../assets/img/hochwasserschutz/l-in-laibung-r-auf-wand.jpg';
import abmessungAufWand from '../../assets/img/hochwasserschutz/abmessungen/auf-wand.png';
import abmessungAufWandVorne from '../../assets/img/hochwasserschutz/abmessungen/auf-wand_vorne.png';
import abmessungInLaibung from '../../assets/img/hochwasserschutz/abmessungen/in-laibung.png';
import abmessungInLaibungVorne from '../../assets/img/hochwasserschutz/abmessungen/in-laibung_vorne.png';
import abmessungLAufWandRInLaibung from '../../assets/img/hochwasserschutz/abmessungen/l-auf-wand-r-in-laibung.png';
import abmessungLAufWandRInLaibungVorne from '../../assets/img/hochwasserschutz/abmessungen/l-auf-wand-r-in-laibung_vorne.png';
import abmessungLInLaibungRAufWand from '../../assets/img/hochwasserschutz/abmessungen/l-in-laibung-r-auf-wand.png';
import abmessungLInLaibungRAufWandVorne from '../../assets/img/hochwasserschutz/abmessungen/l-in-laibung-r-auf-wand_vorne.png';
import Variation from './Variation';
import Card from '../common/Card';
import ModalOpenIcon from '../common/ModalOpenIcon';
import Modal from '../common/Modal';
import Wandschienenhoehe from './Wandschienenhoehe';
import { isAllHidden } from '../../utils/restrictions';
import { getArtikelInfoByArtNr } from '../../utils/plugin-api';
import UnsafeHtml from '../common/UnsafeHtml';
import { appConfig } from '../../config';

const modalAufWand = (
  <div className="templates__hochwasser-messung d-flex flex-column align-items-stretch ">
    <div className="header">Systembreite-Messung bei Montage auf der Wand.</div>
    <div className="abmessungen w-100 d-flex flex-row justify-content-center mt-2">
      <img
        src={appConfig.basePath + abmessungAufWandVorne}
        alt="Abmessungen"
      />
    </div>
    <div className="laibung w-100 d-flex flex-row justify-content-center mt-5">
      <img
        src={appConfig.basePath + abmessungAufWand}
        alt="Auf der Wand"
      />
    </div>
  </div>
);
const modalInLaibung = (
  <div className="templates__hochwasser-messung d-flex flex-column align-items-stretch position-relative">
    <div className="header">Systembreite-Messung bei Montage in der Laibung.</div>
    <div className="abmessungen w-100 d-flex flex-row justify-content-center mt-2">
      <img
        src={appConfig.basePath + abmessungInLaibungVorne}
        alt="Abmessungen"
      />
    </div>
    <div className="laibung w-100 d-flex flex-row justify-content-center mt-5">
      <img
        src={appConfig.basePath + abmessungInLaibung}
        alt="In der Laibung"
      />
    </div>
  </div>
);
const modalLAufWandRInLaibung = (
  <div className="templates__hochwasser-messung d-flex flex-column align-items-stretch position-relative">
    <div className="header">Systembreite-Messung bei Montage links auf der Wand, rechts in der Laibung.</div>
    <div className="abmessungen w-100 d-flex flex-row justify-content-center mt-2">
      <img
        src={appConfig.basePath + abmessungLAufWandRInLaibungVorne}
        alt="Abmessungen"
      />
    </div>
    <div className="laibung w-100 d-flex flex-row justify-content-center mt-5">
      <img
        src={appConfig.basePath + abmessungLAufWandRInLaibung}
        alt="Links auf der Wand, rechts in der Laibung"
      />
    </div>
  </div>
);
const modalLInLaibungRAufWand = (
  <div className="templates__hochwasser-messung d-flex flex-column align-items-stretch position-relative">
    <div className="header">Systembreite-Messung bei Montage links in der Laibung, rechts auf der Wand.</div>
    <div className="abmessungen w-100 d-flex flex-row justify-content-center mt-2">
      <img
        src={appConfig.basePath + abmessungLInLaibungRAufWandVorne}
        alt="Abmessungen"
      />
    </div>
    <div className="laibung w-100 d-flex flex-row justify-content-center mt-5">
      <img
        src={appConfig.basePath + abmessungLInLaibungRAufWand}
        alt="Links in der Laibung, rechts auf der Wand"
      />
    </div>
  </div>
);

function Hochwasserschutz() {
  const { appData, setDynamicAppData } = useContext(AppContext);

  const [breite, setBreite] = useState(getBreite(appData));
  const [validations, setValidations] = useState([] as ValidationMessage[]);
  const [modalVerstaerkung, setModalVerstaerkung] = useState<React.ReactElement>();

  const checkValidity = (data: AppData): boolean => {
    const checkedValidations = validateBreite(data);
    setValidations(checkedValidations);
    return !checkedValidations.find((v) => v.severity === 'error');
  };

  const inputColor = (error: boolean, warning: boolean) => {
    if (error) {
      return 'error';
    }
    if (warning) {
      return 'warning';
    }
    return 'valid';
  };

  const isError = () => !!validations.find((v) => v.severity === 'error'
    && v.kEigenschaft.includes(appData.static.kEigenschaftBreite));
  const isWarning = () => !!validations.find((v) => v.severity === 'warning'
    && v.kEigenschaft.includes(appData.static.kEigenschaftBreite));
  const inputColorLaenge = inputColor(isError(), isWarning());

  useEffect(() => {
    const newAppData = { ...appData };
    newAppData.dynamic.selectedVariationWerte[appData.static.kEigenschaftBreite] = breite;
    const valid = checkValidity(newAppData);

    setDynamicAppData((dynamicAppData) => {
      const newDynamicAppData = { ...dynamicAppData };
      newDynamicAppData.invalid = !valid;
      newDynamicAppData.selectedVariationWerte[appData.static.kEigenschaftBreite] = breite;
      return newDynamicAppData;
    });
  }, [breite]); // eslint-disable-line react-hooks/exhaustive-deps

  const variationWertMontageart = getSelectedVariationWert(
    appData.static.artikel,
    appData.dynamic.selectedVariationWerte,
    appData.static.kEigenschaftMontageart
  );
  let bild;
  let infoModalContent;
  if (variationWertMontageart.cName.toLowerCase().includes('links auf der wand')) {
    infoModalContent = modalLAufWandRInLaibung;
    bild = lAufWandRInLaibung;
  } else if (variationWertMontageart.cName.toLowerCase().includes('links in der laibung')) {
    infoModalContent = modalLInLaibungRAufWand;
    bild = lInLaibungRAufWand;
  } else if (variationWertMontageart.cName.toLowerCase().includes('auf der wand')) {
    infoModalContent = modalAufWand;
    bild = aufWand;
  } else if (variationWertMontageart.cName.toLowerCase().includes('in der laibung')) {
    infoModalContent = modalInLaibung;
    bild = inLaibung;
  }

  const verstaerkungHidden = useMemo(() => isAllHidden(
    appData.static.artikel,
    appData.static.config,
    appData.dynamic.selectedVariationWerte,
    appData.static.kEigenschaftVerstaerkung
  ), [appData.dynamic.selectedVariationWerte[appData.static.kEigenschaftBreite]]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (verstaerkungHidden) {
      setDynamicAppData((dynamicAppData) => {
        const newDynamicAppData = { ...dynamicAppData };
        delete appData.dynamic.selectedVariationWerte[appData.static.kEigenschaftVerstaerkung];
        return newDynamicAppData;
      });
    } else if (!appData.dynamic.selectedVariationWerte[appData.static.kEigenschaftVerstaerkung]) {
      setDynamicAppData((dynamicAppData) => {
        const wert = getFirstUnrestrictedVariationWert(
          appData.static.artikel,
          appData.static.config,
          dynamicAppData.selectedVariationWerte,
          appData.static.kEigenschaftVerstaerkung
        );
        const newDynamicAppData = { ...dynamicAppData };
        newDynamicAppData.selectedVariationWerte[appData.static.kEigenschaftVerstaerkung] = wert.kEigenschaftWert;
        return newDynamicAppData;
      });
    }
  }, [appData.dynamic.selectedVariationWerte[appData.static.kEigenschaftBreite]]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedVerstaerkung = appData.dynamic.selectedVariationWerte[appData.static.kEigenschaftVerstaerkung];
  useEffect(() => {
    const getVerstaerkungDetails = async () => {
      if (selectedVerstaerkung) {
        const verstaerkung = getSelectedVariationWert(appData.static.artikel, appData.dynamic.selectedVariationWerte, appData.static.kEigenschaftVerstaerkung);
        // Sonderfall: ArtNr 0 signalisiert, dass die Variation keinem Artikel zugewiesen wurde.
        if (verstaerkung.cArtNr !== '0') {
          const artikelVerstaerkung = await getArtikelInfoByArtNr(verstaerkung.cArtNr);
          setModalVerstaerkung((
            <div>
              <div><UnsafeHtml unsafeHtml={artikelVerstaerkung.name ?? ''} /></div>
              <div className="verstaerkung-bilder d-flex flex-row flex-wrap gap-2 justify-content-between">
                {artikelVerstaerkung.bilder
                  .filter((b) => b?.cURLNormal)
                  .map((b) => (
                    <img key={b.nNr} src={b.cURLNormal} alt="" />
                  ))}
              </div>
            </div>
          ));
        }
      }
    };
    getVerstaerkungDetails();
  }, [selectedVerstaerkung]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="configurator__hochwasserschutz">
      <Card>
        <div>
          <Variation kEigenschaft={appData.static.kEigenschaftMontageart} withoutPrice />
        </div>
      </Card>
      <Card className="mt-2">
        <div className="d-flex flex-row justify-content-between align-items-center">
          <h3>Systembreite</h3>
          <ModalOpenIcon modalId={`infoModal${appData.static.kEigenschaftBreite}`} />
          <Modal
            id={`infoModal${appData.static.kEigenschaftBreite}`}
            title="Systembreite"
          >
            {infoModalContent}
          </Modal>
        </div>
        <hr />
        <div className="mt-3 position-relative">
          <div className="image w-100">
            <img src={appConfig.basePath + bild} alt="" />
          </div>
          <div className="desc position-absolute">
            <div className="inputs">
              <div className="input-group mb-2">
                <div className="input-group-prepend">
                  <span className="input-group-text">Breite</span>
                </div>
                <input
                  className={`${inputColorLaenge} form-control`}
                  type="text"
                  value={breite}
                  placeholder="Breite"
                  pattern="^[0-9]*$"
                  onChange={(e) => {
                    if (!Number.isNaN(Number(e.target.value))) {
                      setBreite(Number(e.target.value));
                    }
                  }}
                />
                <div className="input-group-append">
                  <span className="input-group-text">mm</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          {validations.map((v) => v.messageContent)}
        </div>
      </Card>
      <Card key={appData.static.kEigenschaftStauhoehe} className="mt-2">
        <Variation kEigenschaft={appData.static.kEigenschaftStauhoehe} withoutPrice />
      </Card>
      <Card key={appData.static.kEigenschaftWandschienenhoehe} className="mt-2">
        <h3>Wandschienenhöhe</h3>
        <hr />
        <Wandschienenhoehe />
      </Card>
      {!verstaerkungHidden
        && (
          <Card key={appData.static.kEigenschaftVerstaerkung} className="mt-2">
            <Variation kEigenschaft={appData.static.kEigenschaftVerstaerkung} withoutPrice={false} infoModalContent={modalVerstaerkung} />
          </Card>
        )}
      {appData.static.kEigenschaftVersand
        && (
          <Card key={appData.static.kEigenschaftVersand} className="mt-2">
            <Variation kEigenschaft={appData.static.kEigenschaftVersand} withoutPrice={false} />
          </Card>
        )}
    </div>
  );
}

export default Hochwasserschutz;
